import { IError } from "../types/errors.d";
import { ActionType } from "../types/store-types";

export enum UserRoles {
    ADMIN,
    MODERATOR,
    USER,
    GUEST,
}

export enum IUserTypes {
    GUEST = "GUEST",
    USER = "USER",
}

export interface IUser {
    _id: string;
    name: string;
    email: string;
    isActive: boolean;
    pic?: string | null;
    role: IUserTypes;
    createdAt: string;
    updatedAt: string;
}

/* - Data Types - */

/* -- Init user Request Data */
export interface IInitUserRequestData {}

export interface IInitUserSuccessData {
    token: string;
    refresh_token: string;
    userType: IUserTypes;
}

export interface IInitUserFailureData extends IError {}

/* -- Sign In user Request Data */

export interface ISignInRequestData {
    email: string;
    password: string;
}

export interface ISignInSuccessData {
    token: string;
    refresh_token: string;
}

export interface ISignInFailureData extends IError {}

/* -- Sign Up user Request Data */

export interface ISignUpRequestData {
    name: string;
    email: string;
    password: string;
    redirect_url: string;
}

export interface ISignUpSuccessData extends IUser {}

export interface ISignUpFailureData extends IError {}

/* -- Confirm user Request Data */

export interface IConfirmUserRequestData {
    code: string;
}

export interface IConfirmUserSuccessData {}

export interface IConfirmUserFailureData extends IError {}

export interface ISetUserToken {
    token: string;
    refresh_token: string;
    userType?: UserRoles;
}

export interface ISetLoadingData {
    isLoading: boolean;
}

/* Get Current User Data */
export interface IGetCurrentUserData {}

export interface IGetCurrentUserSuccess {
    first_name: string;
    last_name: string;
    email: string;
    role: UserRoles;
    pic: string | null;
    _id: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

/* Migrate user request data  */
export interface IMigrateGuestRequest {
    userId: string;
    userRefreshToken: string;
}
export interface IMigrateGuestFailure {}
export interface IMigrateGuestSuccess {
    userId: string;
    userRefreshToken: string;
}

export interface IClearErrorsData {}

export interface IGetHubspotAuthURLRequestData {
    formId: string;
}

export interface IGetHubspotAuthURLSuccessData {
    success: boolean;
    message: string;
    data: string;
}

export interface IGetHubspotAuthURLFailureData extends IError {
    success: boolean;
    message: string;
    data: string;
}

export interface IGetHubspotDataRequestData {
    formId: string;
}

export interface IGetHubspotDataSuccessData {
    hubspotData: {
        table: string;
        fields: {
            [key: string]: string;
        };
        _id: string;
    };
}

export interface IGetHubspotDataFailureData extends IError {}

export interface IGetHubspotStatusRequestData {
    formId: string;
}

export interface IGetHubspotStatusSuccessData {
    success: boolean;
    message: string;
    data: string;
}

export interface IGetHubspotStatusFailureData extends IError {}

export interface IUpdateHubspotPropertyData {
    tableName: string;
    fields: {
        [key: string]: string;
    };
    _id?: string;
}

export interface IUpdateHubspotPropertyRequestData {
    formId: string;
    tableName: string;
    fields: {
        [key: string]: string;
    };
}
export interface IUpdateHubspotPropertySuccessData {
    success: boolean;
    message: string;
    data: IUpdateHubspotPropertyData | null;
}

export interface IUpdateHubspotPropertyFailureData extends IError {}

export interface IGetHubspotTablesData {
    data: Array<Record<string, any>>;
}

export interface IGetHubspotTablesRequestData {
    formId: string;
    table: string;
}
export interface IGetHubspotTablesSuccessData {
    success: boolean;
    message: string;
    data: IGetHubspotTablesData | null;
}

export interface IGetHubspotTablesFailureData extends IError {}

export interface IInitHubspotRequestData {
    formId: string;
}

export interface IInitHubspotSuccessData {
    success: boolean;
    message: string;
    data: string;
}

export interface IInitHubspotFailureData extends IError {}

/* - State - */

export interface IAuthState {
    user: IUser | null;
    userType: IUserTypes;
    token: string | null;
    refresh_token: string | null;
    errors: null | IError;
    isLoading: boolean;
    zapierAuthURL?: string;
}

export interface IHubspotState {
    isLoading?: boolean;
    authUrl?: string | null;
    error?: string | null | IError;
    hubspotData?: IGetHubspotDataSuccessData | null;
    hubspotTables?: IGetHubspotTablesData | null;
    status?: string | null;
    message?: string | null;
    success?: string | null | boolean;
    hubspoTablesData?: IGetHubspotTablesData | null;
}

export interface IHubspotStatusState {
    status?: string | null;
    message?: string | null;
    success?: string | null | boolean;
}
export interface IHubspotSDataState {
    hubspotData?: IGetHubspotDataSuccessData | null;
    message?: string | null;
    success?: string | null | boolean;
}

/* - Action Types - */

export enum AuthActionTypes {
    INIT_USER_REQUEST = "AUTH.INIT_USER_REQUEST",
    INIT_USER_SUCCESS = "AUTH.INIT_USER_SUCCESS",
    INIT_USER_FAILURE = "AUTH.INIT_USER_FAILURE",

    GET_CURRENT_USER_REQUEST = "AUTH.GET_CURRENT_USER_REQUEST",
    GET_CURRENT_USER_SUCCESS = "AUTH.GET_CURRENT_USER_SUCCESS",
    GET_CURRENT_USER_FAILURE = "AUTH.GET_CURRENT_USER_FAILURE",

    MIGRATE_GUEST_TO_USER_REQUEST = "AUTH.MIGRATE_GUEST_TO_USER_REQUEST",
    MIGRATE_GUEST_TO_USER_SUCCESS = "AUTH.MIGRATE_GUEST_TO_USER_SUCCESS",
    MIGRATE_GUEST_TO_USER_FAILURE = "AUTH.MIGRATE_GUEST_TO_USER_FAILURE",

    SET_USER_TOKEN = "AUTH.SET_USER_TOKEN",
    SET_LOADING = "AUTH.SET_LOADING",
    CLEAR_ERRORS = "AUTH.CLEAR_ERRORS",

    GET_ZAPIER_AUTH_URL_REQUEST = "AUTH.GET_ZAPIER_AUTH_URL_REQUEST",
    GET_ZAPIER_AUTH_URL_SUCCESS = "AUTH.GET_ZAPIER_AUTH_URL_SUCCESS",
    GET_ZAPIER_AUTH_URL_FAILURE = "AUTH.GET_ZAPIER_AUTH_URL_FAILURE",

    GET_HUBSPOT_AUTH_URL_REQUEST = "AUTH.GET_HUBSPOT_AUTH_URL_REQUEST",
    GET_HUBSPOT_AUTH_URL_SUCCESS = "AUTH.GET_HUBSPOT_AUTH_URL_SUCCESS",
    GET_HUBSPOT_AUTH_URL_FAILURE = "AUTH.GET_HUBSPOT_AUTH_URL_FAILURE",

    GET_HUBSPOT_DATA_REQUEST = "AUTH.GET_HUBSPOT_DATA_REQUEST",
    GET_HUBSPOT_DATA_SUCCESS = "AUTH.GET_HUBSPOT_DATA_SUCCESS",
    GET_HUBSPOT_DATA_FAILURE = "AUTH.GET_HUBSPOT_DATA_FAILURE",

    GET_HUBSPOT_STATUS_REQUEST = "AUTH.GET_HUBSPOT_STATUS_REQUEST",
    GET_HUBSPOT_STATUS_SUCCESS = "AUTH.GET_HUBSPOT_STATUS_SUCCESS",
    GET_HUBSPOT_STATUS_FAILURE = "AUTH.GET_HUBSPOT_STATUS_FAILURE",

    UPDATE_HUBSPOT_PROPERTY_REQUEST = "AUTH.UPDATE_HUBSPOT_PROPERTY_REQUEST",
    UPDATE_HUBSPOT_PROPERTY_SUCCESS = "AUTH.UPDATE_HUBSPOT_PROPERTY_SUCCESS",
    UPDATE_HUBSPOT_PROPERTY_FAILURE = "AUTH.UPDATE_HUBSPOT_PROPERTY_FAILURE",

    GET_HUBSPOT_TABLES_REQUEST = "AUTH.GET_HUBSPOT_TABLES_REQUEST",
    GET_HUBSPOT_TABLES_SUCCESS = "AUTH.GET_HUBSPOT_TABLES_SUCCESS",
    GET_HUBSPOT_TABLES_FAILURE = "AUTH.GET_HUBSPOT_TABLES_FAILURE",

    INIT_HUBSPOT_REQUEST = "AUTH.INIT_HUBSPOT_REQUEST",
    INIT_HUBSPOT_SUCCESS = "AUTH.INIT_HUBSPOT_SUCCESS",
    INIT_HUBSPOT_FAILURE = "AUTH.INIT_HUBSPOT_FAILURE",

    DISCONNECT_INTEGRATION_REQUEST = "AUTH.DISCONNECT_INTEGRATION_REQUEST",
    DISCONNECT_INTEGRATION_SUCCESS = "AUTH.DISCONNECT_INTEGRATION_SUCCESS",
    DISCONNECT_INTEGRATION_FAILURE = "AUTH.DISCONNECT_INTEGRATION_FAILURE",
}

/* - Action Interfaces  - */

export type InitUserRequestActionType = ActionType<AuthActionTypes.INIT_USER_REQUEST, IInitUserRequestData>;
export type InitUserSuccessActionType = ActionType<AuthActionTypes.INIT_USER_SUCCESS, IInitUserSuccessData>;
export type InitUserFailureActionType = ActionType<AuthActionTypes.INIT_USER_FAILURE, IInitUserFailureData>;

export type GetCurrentUserRequestActionType = ActionType<AuthActionTypes.GET_CURRENT_USER_REQUEST, IGetCurrentUserData>;
export type GetCurrentUserSuccessActionType = ActionType<
    AuthActionTypes.GET_CURRENT_USER_SUCCESS,
    IGetCurrentUserSuccess
>;
export type GetCurrentUserFailureActionType = ActionType<AuthActionTypes.GET_CURRENT_USER_FAILURE, IGetCurrentUserData>;

export type MigrateGuestToUserRequestActionType = ActionType<
    AuthActionTypes.MIGRATE_GUEST_TO_USER_REQUEST,
    IMigrateGuestRequest
>;
export type MigrateGuestToUserSuccessActionType = ActionType<
    AuthActionTypes.MIGRATE_GUEST_TO_USER_SUCCESS,
    IMigrateGuestSuccess
>;
export type MigrateGuestToUserFailureActionType = ActionType<
    AuthActionTypes.MIGRATE_GUEST_TO_USER_FAILURE,
    IMigrateGuestFailure
>;

export type SetUserTokenActionType = ActionType<AuthActionTypes.SET_USER_TOKEN, ISetUserToken>;
export type SetLoadingActionType = ActionType<AuthActionTypes.SET_LOADING, ISetLoadingData>;
export type ClearErrorsActionType = ActionType<AuthActionTypes.CLEAR_ERRORS, IClearErrorsData>;

/* -- Get Zapier Auth URL Request Data */
export interface IZapierState {
    isLoading: boolean;
    authUrl: string | null;
    formId: string | null;
    error: string | null;
}
export interface IGetZapierAuthURLRequestData {
    formId: string;
}

export interface IGetZapierAuthURLSuccessData {
    authUrl: string;
    formId: string;
}

export interface IGetZapierAuthURLFailureData extends IError {}

/* - Action Interfaces - */
export type GetZapierAuthURLRequestActionType = {
    type: AuthActionTypes.GET_ZAPIER_AUTH_URL_REQUEST;
};

export type GetZapierAuthURLSuccessActionType = ActionType<
    AuthActionTypes.GET_ZAPIER_AUTH_URL_SUCCESS,
    IGetZapierAuthURLSuccessData
>;
export type GetZapierAuthURLFailureActionType = ActionType<
    AuthActionTypes.GET_ZAPIER_AUTH_URL_FAILURE,
    IGetZapierAuthURLFailureData
>;
export interface IDisconnectIntegrationState {
    success: boolean;
    message: string | null;
    data: IDisconnectIntegrationData | null;
}

export interface IDisconnectIntegrationData {
    userId: string;
    formId: string;
    statusess: {
        googleSheet: boolean;
        mailChimp: boolean;
        zapier: boolean;
    };
    accounts: {
        googleSheet: {
            access_token: string;
            refresh_token: string;
            scope: string;
            token_type: string;
            expiry_date: number;
        };
        mailChimp?: {
            access_token: string;
            refresh_token: string;
            dc: string;
        };
        zapier?: {
            access_token: string;
            refresh_token: string;
        };
    };
    config: {
        googleSheet: {
            targetSheet: string;
            fields: string[];
            subSheetName?: string;
            startFrom?: string;
        };
        mailChimp?: {
            listId: string;
            fields: Record<string, any>;
        };
        zapier?: Record<string, any>;
    };
}

export interface IDisconnectIntegrationSuccessData {
    success: boolean;
    message: string;
    data: IDisconnectIntegrationData;
}

export interface IDisconnectIntegrationFailureData extends IError {}

export type GetHubspotAuthURLRequestActionType = {
    type: AuthActionTypes.GET_HUBSPOT_AUTH_URL_REQUEST;
};
export type GetHubspotAuthURLSuccessActionType = ActionType<
    AuthActionTypes.GET_HUBSPOT_AUTH_URL_SUCCESS,
    IGetHubspotAuthURLSuccessData
>;
export type GetHubspotAuthURLFailureActionType = ActionType<
    AuthActionTypes.GET_HUBSPOT_AUTH_URL_FAILURE,
    IGetHubspotAuthURLFailureData
>;
export type GetHubspotDataRequestActionType = {
    type: AuthActionTypes.GET_HUBSPOT_DATA_REQUEST;
};
export type GetHubspotDataSuccessActionType = ActionType<
    AuthActionTypes.GET_HUBSPOT_DATA_SUCCESS,
    IGetHubspotDataSuccessData
>;
export type GetHubspotDataFailureActionType = ActionType<
    AuthActionTypes.GET_HUBSPOT_DATA_FAILURE,
    IGetHubspotDataFailureData
>;
export type GetHubspotStatusRequestActionType = {
    type: AuthActionTypes.GET_HUBSPOT_STATUS_REQUEST;
};
export type GetHubspotStatusSuccessActionType = ActionType<
    AuthActionTypes.GET_HUBSPOT_STATUS_SUCCESS,
    IGetHubspotStatusSuccessData
>;
export type GetHubspotStatusFailureActionType = ActionType<
    AuthActionTypes.GET_HUBSPOT_STATUS_FAILURE,
    IGetHubspotStatusFailureData
>;

export type UpdateHubspotPropertyRequestActionType = ActionType<
    AuthActionTypes.UPDATE_HUBSPOT_PROPERTY_REQUEST,
    IUpdateHubspotPropertyRequestData
>;
export type UpdateHubspotPropertySuccessActionType = ActionType<
    AuthActionTypes.UPDATE_HUBSPOT_PROPERTY_SUCCESS,
    IUpdateHubspotPropertySuccessData
>;
export type UpdateHubspotPropertyFailureActionType = ActionType<
    AuthActionTypes.UPDATE_HUBSPOT_PROPERTY_FAILURE,
    IUpdateHubspotPropertyFailureData
>;

export type GetHubspotTablesRequestActionType = ActionType<
    AuthActionTypes.GET_HUBSPOT_TABLES_REQUEST,
    IGetHubspotTablesRequestData
>;
export type GetHubspotTablesSuccessActionType = ActionType<
    AuthActionTypes.GET_HUBSPOT_TABLES_SUCCESS,
    IGetHubspotTablesSuccessData
>;
export type GetHubspotTablesFailureActionType = ActionType<
    AuthActionTypes.GET_HUBSPOT_TABLES_FAILURE,
    IGetHubspotTablesFailureData
>;
export type InitHubspotRequestActionType = {
    type: AuthActionTypes.INIT_HUBSPOT_REQUEST;
};
export type InitHubspotSuccessActionType = ActionType<AuthActionTypes.INIT_HUBSPOT_SUCCESS, IInitHubspotSuccessData>;
export type InitHubspotFailureActionType = ActionType<AuthActionTypes.INIT_HUBSPOT_FAILURE, IInitHubspotFailureData>;

export interface IDisconnectIntegrationRequestData {
    type: string;
}

export type DisconnectIntegrationRequestActionType = ActionType<
    AuthActionTypes.DISCONNECT_INTEGRATION_REQUEST,
    IDisconnectIntegrationRequestData
>;

export type DisconnectIntegrationSuccessActionType = ActionType<
    AuthActionTypes.DISCONNECT_INTEGRATION_SUCCESS,
    IDisconnectIntegrationSuccessData
>;

export type DisconnectIntegrationFailureActionType = ActionType<
    AuthActionTypes.DISCONNECT_INTEGRATION_FAILURE,
    IDisconnectIntegrationFailureData
>;
